export type FooterLinkType = 'url' | 'page';

export interface FooterQuickLink {
  id: string;
  label: string;
  url: string;
  column: number;
  linkType?: FooterLinkType;
  pageKey?: string;
  scrollTarget?: string;
  tabId?: string;
  sectionId?: string;
  subTabId?: string;
  locationLabel?: string;
}

export interface FooterPageOption {
  value: string;
  label: string;
  group: string;
}

export interface FooterLocationOption {
  value: string;
  label: string;
  group: string;
  scrollTarget?: string;
  tabId?: string;
  sectionId?: string;
  subTabId?: string;
}

export const FOOTER_PAGE_OPTIONS: FooterPageOption[] = [
  { value: 'home', label: 'Ana Sayfa', group: 'Sabit sayfalar' },
  { value: 'about', label: 'Hakkımızda', group: 'Sabit sayfalar' },
  { value: 'services', label: 'Hizmetler', group: 'Sabit sayfalar' },
  { value: 'products-solutions', label: 'Ürün & Çözümler', group: 'Sabit sayfalar' },
  { value: 'references', label: 'Referanslar', group: 'Sabit sayfalar' },
  { value: 'products', label: 'Ürünler', group: 'Sabit sayfalar' },
  { value: 'contact', label: 'İletişim', group: 'Sabit sayfalar' },
  { value: 'faq', label: 'SSS (Hesap)', group: 'Sabit sayfalar' },
];

const PAGE_PATHS: Record<string, string> = {
  home: '/',
  about: '/about',
  services: '/services',
  'products-solutions': '/products-solutions',
  references: '/references',
  contact: '/contact',
  products: '/products',
  faq: '/account/faq',
  branches: '/branches',
  'become-seller': '/become-seller',
  cart: '/cart',
};

const PATH_TO_PAGE_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(PAGE_PATHS).map(([key, path]) => [path, key])
);

const TAB_TARGET_CONFIGS = [
  { pageKey: 'about', pageType: 'AboutTab', group: 'Hakkımızda sekmeleri' },
  { pageKey: 'services', pageType: 'ServiceTab', group: 'Hizmetler sekmeleri' },
  { pageKey: 'products-solutions', pageType: 'ProductSolutionTab', group: 'Ürün & Çözümler sekmeleri' },
  { pageKey: 'references', pageType: 'ReferenceTab', group: 'Referanslar sekmeleri' },
] as const;

type TabSection = { id: string; title: string };

const parseSections = (sections: unknown): TabSection[] => {
  let arr: any = sections;
  if (typeof sections === 'string') {
    try {
      arr = JSON.parse(sections);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr)) {
    if (arr && Array.isArray(arr.sections)) arr = arr.sections;
    else return [];
  }
  return arr
    .filter((s: any) => s && s.id && (s.title || s.name))
    .map((s: any) => ({ id: String(s.id), title: String(s.title || s.name) }));
};

const tabLabel = (tab: any): string => String(tab?.title || tab?.name || 'İsimsiz Sekme');
const isTabActive = (tab: any): boolean => tab?.isActive !== false && tab?.IsActive !== false;

export function buildPageLinkUrl(options: {
  pageKey: string;
  scrollTarget?: string;
  tabId?: string;
  sectionId?: string;
  subTabId?: string;
}): string {
  const { pageKey, scrollTarget, tabId, sectionId, subTabId } = options;
  let path = PAGE_PATHS[pageKey] || '/';
  if (pageKey.startsWith('custom:')) {
    const slug = pageKey.slice('custom:'.length).trim();
    path = slug ? `/pages/${slug}` : '/';
  }

  const params = new URLSearchParams();
  if (tabId) params.set('tab', tabId);
  if (sectionId) params.set('section', sectionId);
  if (subTabId) params.set('subtab', subTabId);
  if (scrollTarget && scrollTarget !== 'top') params.set('scroll', scrollTarget);

  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export function parsePageLinkUrl(url: string): {
  pageKey: string;
  tabId?: string;
  sectionId?: string;
  subTabId?: string;
  scrollTarget?: string;
} | null {
  const raw = String(url || '').trim();
  if (!raw || /^(https?:|mailto:|tel:)/i.test(raw)) return null;

  try {
    const normalized = raw.startsWith('/') ? raw : `/${raw}`;
    const parsed = new URL(normalized, 'https://local.invalid');
    const pathname = (parsed.pathname.replace(/\/+$/, '') || '/') as string;
    const params = parsed.searchParams;

    let pageKey = PATH_TO_PAGE_KEY[pathname];
    if (!pageKey && pathname.startsWith('/pages/')) {
      const slug = pathname.slice('/pages/'.length).trim();
      if (slug) pageKey = `custom:${slug}`;
    }
    if (!pageKey) return null;

    return {
      pageKey,
      tabId: params.get('tab') || undefined,
      sectionId: params.get('section') || undefined,
      subTabId: params.get('subtab') || undefined,
      scrollTarget: params.get('scroll') || undefined
    };
  } catch {
    return null;
  }
}

export function getStaticLocationsForPage(pageKey: string): FooterLocationOption[] {
  const locations: FooterLocationOption[] = [
    { value: 'top', label: 'Sayfa başı', group: 'Sayfa bölümleri', scrollTarget: 'top' },
  ];

  if (pageKey === 'about') {
    locations.push(
      { value: 'about-story', label: 'Hikayemiz', group: 'Sayfa bölümleri', scrollTarget: 'about-story', tabId: 'main' },
      { value: 'about-values', label: 'Değerlerimiz', group: 'Sayfa bölümleri', scrollTarget: 'about-values', tabId: 'main' },
      {
        value: 'about-why-choose',
        label: 'Neden bizi tercih etmelisiniz',
        group: 'Sayfa bölümleri',
        scrollTarget: 'about-why-choose',
        tabId: 'main',
      }
    );
  }

  if (pageKey !== 'contact' && pageKey !== 'faq') {
    locations.push({
      value: 'faq',
      label: 'SSS (alt içerik)',
      group: 'Sayfa bölümleri',
      scrollTarget: 'faq',
    });
  }

  return locations;
}

export function buildTabLocations(pageKey: string, tabs: any[]): FooterLocationOption[] {
  const config = TAB_TARGET_CONFIGS.find((item) => item.pageKey === pageKey);
  if (!config || !Array.isArray(tabs) || tabs.length === 0) return [];

  const sorted = [...tabs]
    .filter(isTabActive)
    .sort((a, b) => (Number(a?.displayOrder) || 0) - (Number(b?.displayOrder) || 0));
  const mains = sorted.filter((tab) => !tab?.parentId || tab?.level === 0);
  const mainIds = new Set(mains.map((main) => String(main.id)));
  const subsByParent = new Map<string, any[]>();
  sorted
    .filter((tab) => tab?.parentId && mainIds.has(String(tab.parentId)))
    .forEach((tab) => {
      const list = subsByParent.get(String(tab.parentId)) || [];
      list.push(tab);
      subsByParent.set(String(tab.parentId), list);
    });

  const options: FooterLocationOption[] = [];
  mains.forEach((main) => {
    const mainId = String(main.id);
    const mainTitle = tabLabel(main);
    options.push({
      value: `tab:${mainId}`,
      label: mainTitle,
      group: config.group,
      tabId: mainId,
    });
    options.push({
      value: `tab-faq:${mainId}`,
      label: `${mainTitle} → SSS`,
      group: config.group,
      tabId: mainId,
      scrollTarget: `faq_tab_${mainId}`,
    });

    parseSections(main.sections).forEach((section) => {
      options.push({
        value: `section:${mainId}:${section.id}`,
        label: `${mainTitle} → ${section.title}`,
        group: config.group,
        tabId: mainId,
        sectionId: section.id,
        scrollTarget: `section-${section.id}`,
      });
    });

    (subsByParent.get(mainId) || []).forEach((sub) => {
      const subId = String(sub.id);
      const subTitle = tabLabel(sub);
      options.push({
        value: `subtab:${mainId}:${subId}`,
        label: `${mainTitle} → ${subTitle}`,
        group: config.group,
        tabId: mainId,
        subTabId: subId,
        scrollTarget: `subtab-${subId}`,
      });
    });
  });

  return options;
}

export function resolveLocationValue(link: FooterQuickLink): string {
  if (link.subTabId && link.tabId) return `subtab:${link.tabId}:${link.subTabId}`;
  if (link.sectionId && link.tabId) return `section:${link.tabId}:${link.sectionId}`;
  if (link.tabId && link.scrollTarget === `faq_tab_${link.tabId}`) return `tab-faq:${link.tabId}`;
  if (link.tabId && link.tabId !== 'main') return `tab:${link.tabId}`;
  if (link.scrollTarget && link.scrollTarget !== 'top' && link.scrollTarget !== 'faq') {
    return link.scrollTarget;
  }
  if (link.scrollTarget === 'faq') return 'faq';
  return 'top';
}

export function locationValueFromParsed(
  parsed: NonNullable<ReturnType<typeof parsePageLinkUrl>>
): string {
  return resolveLocationValue({
    id: '',
    label: '',
    url: '',
    column: 0,
    tabId: parsed.tabId,
    sectionId: parsed.sectionId,
    subTabId: parsed.subTabId,
    scrollTarget: parsed.scrollTarget
  });
}

export function formatFooterLinkSecondary(link: FooterQuickLink): string {
  if (link.locationLabel?.trim()) return link.locationLabel.trim();
  return link.url || '';
}

export { TAB_TARGET_CONFIGS };
